/**
 * @typedef {import('vue').ComponentOptions<Vue>} VueComponent
 */

/** @type {VueComponent} */
import axios from 'axios';
import Vue from 'vue';

export default {
    props: {
        siteHandle: {
            value: String,
        },
        newsEndpoint: {
            value: String,
        },
    },

    data() {
        return {
            news: [],
            areNewsParsed: false,
            httpClient: null,
            postsPerPage: 3,
        };
    },

    watch: {
        news(newsValue) {
            this.areNewsParsed = Boolean(newsValue.length);
        },
    },

    mounted() {
        this.$watch(
            () => this.$root.csrfTokenValue,
            (newCsrfTokenValue) => {
                if (!newCsrfTokenValue) return;
                this.handleSetNews();
            },
        );
    },

    methods: {
        async handleSetNews() {
            const postsEndpoint = this.getPostsEndpoint();
            const pressEndpoint = this.getPressEndpoint();

            const isNotValidEndpoint = !postsEndpoint || !pressEndpoint;

            if (isNotValidEndpoint) return;
            try {
                const { data: postsData } = await axios.get(postsEndpoint);
                const { data: pressData } = await axios.get(pressEndpoint);

                this.news = await this.parseNews(
                    this.handleSortAndSliceNewsData(postsData, pressData),
                );
            } catch (e) {
                this.news = [];
            }
        },

        handleParseNewsData(newsData) {
            return newsData.map((el) => ({
                title: el.title.rendered,
                description: el.yoast_head_json.description,
                image: el.yoast_head_json.og_image[0].url,
                url: el.link,
                date: new Date(el.date_gmt).getTime(),
            }));
        },

        handleSortAndSliceNewsData(postsData, pressData) {
            const fetchedData = [
                ...this.handleParseNewsData(postsData),
                ...this.handleParseNewsData(pressData),
            ];
            return fetchedData.sort((a, b) => b.date - a.date).slice(0, 3);
        },

        getPostsEndpoint() {
            switch (this.siteHandle) {
                case 'default':
                    return `news/posts?perPage=${this.postsPerPage}`;
                case 'mgMotorsNorway':
                    return `news/posts?language=no&perPage=${this.postsPerPage}`;
                case 'mgMotorsFrance':
                    return `news/posts?language=fr&perPage=${this.postsPerPage}`;
                case 'mgMotorsNetherlands':
                    return `news/posts?language=nl&perPage=${this.postsPerPage}`;
                case 'mgMotorsAustria':
                    return `news/posts?language=at&perPage=${this.postsPerPage}`;
                case 'mgMotorsItaly':
                    return `news/posts?language=it&perPage=${this.postsPerPage}`;
                case 'mgMotorsSpain':
                    return `news/posts?language=es&perPage=${this.postsPerPage}`;
                case 'mgMotorsSweden':
                    return `news/posts?language=sv&perPage=${this.postsPerPage}`;
                case 'mgMotorsBelgiumNl':
                    return `news/posts?language=be-nl&perPage=${this.postsPerPage}`;
                case 'mgMotorsBelgiumFr':
                    return `news/posts?language=be-fr&perPage=${this.postsPerPage}`;
                default:
                    return null;
            }
        },

        getPressEndpoint() {
            switch (this.siteHandle) {
                case 'default':
                    return `news/press?perPage=${this.postsPerPage}`;
                case 'mgMotorsNorway':
                    return `news/press?language=no&perPage=${this.postsPerPage}`;
                case 'mgMotorsFrance':
                    return `news/press?language=fr&perPage=${this.postsPerPage}`;
                case 'mgMotorsNetherlands':
                    return `news/press?language=nl&perPage=${this.postsPerPage}`;
                case 'mgMotorsAustria':
                    return `news/press?language=at&perPage=${this.postsPerPage}`;
                case 'mgMotorsItaly':
                    return `news/press?language=it&perPage=${this.postsPerPage}`;
                case 'mgMotorsSpain':
                    return `news/press?language=es&perPage=${this.postsPerPage}`;
                case 'mgMotorsSweden':
                    return `news/press?language=sv&perPage=${this.postsPerPage}`;
                case 'mgMotorsBelgiumNl':
                    return `news/press?language=be-nl&perPage=${this.postsPerPage}`;
                case 'mgMotorsBelgiumFr':
                    return `news/press?language=be-fr&perPage=${this.postsPerPage}`;
                default:
                    return null;
            }
        },

        getAllNewsEndpoint() {
            switch (this.siteHandle) {
                case 'default':
                    return `${this.newsEndpoint}`;
                case 'mgMotorsNorway':
                    return `${this.newsEndpoint}/no`;
                case 'mgMotorsFrance':
                    return `${this.newsEndpoint}/fr`;
                case 'mgMotorsNetherlands':
                    return `${this.newsEndpoint}/nl`;
                case 'mgMotorsAustria':
                    return `${this.newsEndpoint}/at`;
                case 'mgMotorsItaly':
                    return `${this.newsEndpoint}/it`;
                case 'mgMotorsSpain':
                    return `${this.newsEndpoint}/es`;
                case 'mgMotorsSweden':
                    return `${this.newsEndpoint}/sv`;
                case 'mgMotorsBelgiumNl':
                    return `${this.newsEndpoint}/be-nl`;
                case 'mgMotorsBelgiumFr':
                    return `${this.newsEndpoint}/be-fr`;
                default:
                    return null;
            }
        },

        async parseNews(news) {
            const transformedImages = await Promise.all(
                news.map(({ image }) => this.getTransformedImage(image)),
            );
            news.forEach((data, i) => (data.image = transformedImages[i]));
            return news;
        },

        async getTransformedImage(imageUrl) {
            try {
                const { data } = await axios.post(
                    '/image-transform/news',
                    {
                        imageUrl,
                    },
                    {
                        headers: {
                            'X-CSRF-TOKEN': this.$root.csrfTokenValue,
                        },
                    },
                );

                return {
                    jpegSrcSet: data.srcSetJpeg.split(','),
                    webpSrcSet: data.srcSetWebp.split(','),
                };
            } catch (e) {
                return {};
            }
        },
    },
};
